import React, { forwardRef, memo } from 'react';

import { type XCSS, xcss } from '@atlaskit/primitives';

import { type BaseCellProps } from '../../ui/primitives/base-cell';
import { TD } from '../../ui/primitives/td';
import { TableType } from '../charlie-table';

const InputCellStyle = xcss({
	borderInlineEnd: '1px solid',
	borderColor: 'color.border',
});

const InventoryCellStyle = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':last-of-type': {
		borderInlineEnd: '1px solid',
		borderColor: 'color.border',
	},
});

const RowCellStyle = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':first-of-type': {
		paddingInlineStart: 'space.200',
		borderInlineStart: '1px solid',
		borderColor: 'color.border',
		paddingBlock: 'space.0',
	},
	paddingInlineStart: 'space.200',
	borderBlockEndWidth: '1px',
	borderBlockEndStyle: 'solid',
	paddingBlock: 'space.0',
	borderColor: 'color.border',
	whiteSpace: 'nowrap',
	background: 'inherit',
});

export type RowCellProps = {
	tableType?: TableType;
} & Omit<BaseCellProps, 'as'>;
/**
 * __RowCell__
 *
 * A table row cell. A styled `td` element.
 *
 * - [Examples](https://atlassian.design/components/table/examples)
 */
export const RowCell = memo(
	forwardRef<HTMLTableCellElement, RowCellProps>(
		({ children, testId, xcss: customXcss, tableType = TableType.input, ...props }, ref) => {
			return (
				<TD
					testId={testId}
					xcss={[
						RowCellStyle,
						tableType === TableType.inventory && InventoryCellStyle,
						tableType === TableType.input && InputCellStyle,
						// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- this is inline with what atlaskit primitives do.
						customXcss as XCSS,
					]}
					ref={ref}
					{...props}
				>
					{children}
				</TD>
			);
		},
	),
);
